import {type MatchDetail, MatchStatus} from '@iziping/common';
import React, {useState} from "react";
import './MatchToolBar.css';
import {useTranslation} from "react-i18next";
import LaunchMatch from "../matches/LaunchMatch";
import Modal from "../modal/Modal";
import {useDownloadMatchToPrint, useStandByMatch} from "../matches/matchesHooks";
import { QUERY_CACHE } from '@/model/endpoints';
import {useQueryClient} from "@tanstack/react-query";
import { useNotification } from '@iziping/ui';
import {Square, Pause, Play, Printer} from "lucide-react";
import FinishMatch from "../matches/FinishMatch";


interface MatchToolBarProps {
    match: MatchDetail
}

const MatchToolBar: React.FC<MatchToolBarProps> = ({match}) => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const {notify} = useNotification();
    const [showModal, setShowModal] = useState(false);
    const toggleModal = () => setShowModal(!showModal);
    const closeModal = () => setShowModal(false);

    const [modalContent, setModalContent] = useState<React.ReactNode | null>(null);

    const openModalWith = (component: React.ReactNode) => {
        setModalContent(component);
        setShowModal(true);
    };

    const standByMatch = useStandByMatch();

    const handleStandBy = async (matchId: number) => {
        try {
            await standByMatch.mutateAsync({matchId: matchId});
            await queryClient.invalidateQueries({queryKey: [QUERY_CACHE.MATCHES_SEARCH_PENDING]})
            await queryClient.invalidateQueries({queryKey: [QUERY_CACHE.MATCHES_SEARCH_IN_PROGRESS]})
            await queryClient.invalidateQueries({queryKey: [QUERY_CACHE.MATCHES_BUSY_PLAYERS]})
        } catch (error: any) {
            notify(t(error.error), 'error');
        }
    };

    const { mutateAsync } = useDownloadMatchToPrint();

    const handleOpenPdf = async () => {
        if (!match?.id) return;

        try {
            const response = await mutateAsync({ matchId: match.id });
            const blob = response.data;

            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (err) {
            notify(t('pdf.download.error'), 'error');
        }
    };

    return (
        <>
            {(match.status === MatchStatus.PENDING) ? (
                <span className={'tool-container'}>
                   <span title={t('MATCH_LAUNCH')}><Play size={20} color={'green'} onClick={() => openModalWith(<LaunchMatch match={match} onSuccess={closeModal} />)}/></span>
                   <span title={t('MATCH_PRINT')}><Printer size={20} onClick={handleOpenPdf}/></span>
                </span>
            ) : (match.status === MatchStatus.RUNNING) ? (
                    <span className={'tool-container'}>
                    <span title={t('MATCH_STANDBY')}><Pause color={'orange'} onClick={() => handleStandBy(match.id)}/></span>
                    <span title={t('MATCH_PRINT')}><Printer size={20} onClick={handleOpenPdf}/></span>
                    <span title={t('MATCH_FINISH')}><Square size={20} color={'red'} onClick={() => openModalWith(<FinishMatch id={match.id} onSuccess={closeModal} />)} /></span>

                </span>) :
                <span></span>}

            {showModal && modalContent && (
                <Modal onClose={toggleModal}>
                    {modalContent}
                </Modal>
            )}
        </>
    );
};


export default MatchToolBar;